import React from 'react';
import PropTypes from 'prop-types';

const FeaturesModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const [selectedFeatures, setSelectedFeatures] = React.useState({
    'accidental-death': true,
    'increase-face-amount': true
  });

  const [searchTerm, setSearchTerm] = React.useState('');

  const toggleFeature = featureId => {
    setSelectedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  const clearAllSelections = () => {
    setSelectedFeatures({});
  };

  const getSelectedCount = () => {
    return Object.values(selectedFeatures).filter(Boolean).length;
  };

  const handleSearch = () => {
    // Implement search functionality here
    onClose();
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto',
      position: 'relative',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e7e6e6'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#6c757d',
      padding: '4px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      transition: 'all 0.2s ease'
    },
    searchContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px'
    },
    searchInput: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      fontSize: '14px',
      outline: 'none',
      marginLeft: '8px',
      color: '#333'
    },
    searchIcon: {
      fontSize: '18px',
      color: '#6c757d'
    },
    clearButton: {
      background: 'none',
      border: 'none',
      fontSize: '14px',
      color: '#6c757d',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    content: {
      color: '#333',
      lineHeight: '1.6'
    },
    section: {
      marginBottom: '24px'
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '12px'
    },
    buttonGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '16px'
    },
    featureButton: {
      padding: '8px 16px',
      border: '1px solid #dee2e6',
      borderRadius: '20px',
      backgroundColor: '#f8f9fa',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#6c757d',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    featureButtonSelected: {
      backgroundColor: '#333',
      color: '#fff',
      borderColor: '#333'
    },
    featureButtonHover: {
      backgroundColor: '#e9ecef',
      borderColor: '#adb5bd'
    },
    footer: {
      borderTop: '1px solid #e7e6e6',
      paddingTop: '16px',
      marginTop: '24px',
      display: 'flex',
      justifyContent: 'flex-end'
    },
    searchResultsButton: {
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }
  };

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getButtonStyle = featureId => ({
    ...styles.featureButton,
    ...(selectedFeatures[featureId] ? styles.featureButtonSelected : {})
  });

  // All available features for searching
  const allFeatures = [
    { id: 'accidental-death', label: 'Accidental Death', section: 'features' },
    { id: 'child-rider', label: 'Child Rider', section: 'features' },
    { id: 'premium-waiver', label: 'Premium Waiver', section: 'features' },
    { id: 'return-premium', label: 'Return of Premium', section: 'features' },
    { id: 'chronic-illness', label: 'Chronic Illness', section: 'adb-rider' },
    { id: 'critical-illness', label: 'Critical Illness', section: 'adb-rider' },
    { id: 'terminal-illness', label: 'Terminal Illness', section: 'adb-rider' },
    { id: 'increase-face-amount', label: 'Increase Face Amount', section: 'flexible-coverage' },
    { id: 'decrease-face-amount', label: 'Decrease Face Amount', section: 'flexible-coverage' },
    { id: 'convertible', label: 'Convertible', section: 'flexible-coverage' },
    { id: 'renewable', label: 'Renewable', section: 'flexible-coverage' },
    { id: 'no-cost-wearable', label: 'No-Cost Wearable', section: 'promotions' },
    { id: 'discount-program', label: 'Discount Program', section: 'promotions' },
    { id: 'accelerated-underwriting', label: 'Accelerated Underwriting', section: 'easy-issue' },
    { id: 'instant-issue', label: 'Instant Issue', section: 'easy-issue' },
    { id: 'non-medical', label: 'Non-Medical', section: 'easy-issue' },
    { id: 'common-carrier', label: 'Common Carrier', section: 'additional-riders' },
    { id: 'col-increase', label: 'CoL Increase', section: 'additional-riders' },
    { id: 'family-health', label: 'Family Health', section: 'additional-riders' },
    { id: 'nursing-home', label: 'Nursing Home Confinement', section: 'additional-riders' },
    { id: 'residential-damage', label: 'Residential Damage', section: 'additional-riders' }
  ];

  const filteredFeatures = allFeatures.filter(feature => feature.label.toLowerCase().includes(searchTerm.toLowerCase()));

  const shouldShowSection = sectionFeatures => {
    if (!searchTerm) return true;
    return sectionFeatures.some(featureId =>
      allFeatures
        .find(f => f.id === featureId)
        ?.label.toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  };

  const shouldShowFeature = featureId => {
    if (!searchTerm) return true;
    const feature = allFeatures.find(f => f.id === featureId);
    return feature?.label.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const FeatureButton = ({ featureId, children }) => {
    if (!shouldShowFeature(featureId)) return null;

    return (
      <button
        onClick={() => toggleFeature(featureId)}
        onMouseEnter={e => {
          if (!selectedFeatures[featureId]) {
            Object.assign(e.target.style, styles.featureButtonHover);
          }
        }}
        onMouseLeave={e => {
          if (!selectedFeatures[featureId]) {
            Object.assign(e.target.style, styles.featureButton);
          }
        }}
        style={getButtonStyle(featureId)}
      >
        {children} {selectedFeatures[featureId] ? '✓' : ''}
      </button>
    );
  };

  return (
    <div onClick={handleOverlayClick} style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Features</h2>
          <button
            onClick={onClose}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.color = '#333';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }}
            style={styles.closeButton}
          >
            ×
          </button>
        </div>

        <div style={styles.searchContainer}>
          <span style={styles.searchIcon}>🔍</span>
          <input type='text' placeholder='Search features...' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={styles.searchInput} />
          {getSelectedCount() > 0 && (
            <button onClick={clearAllSelections} style={styles.clearButton}>
              × Clear {getSelectedCount()} Selected
            </button>
          )}
        </div>

        <div style={styles.content}>
          {shouldShowSection(['accidental-death', 'child-rider', 'premium-waiver', 'return-premium']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Features</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='accidental-death'>Accidental Death</FeatureButton>
                <FeatureButton featureId='child-rider'>Child Rider</FeatureButton>
                <FeatureButton featureId='premium-waiver'>Premium Waiver</FeatureButton>
              </div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='return-premium'>Return of Premium</FeatureButton>
              </div>
            </div>
          )}

          {shouldShowSection(['chronic-illness', 'critical-illness', 'terminal-illness']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>ADB Rider</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='chronic-illness'>Chronic Illness</FeatureButton>
                <FeatureButton featureId='critical-illness'>Critical Illness</FeatureButton>
                <FeatureButton featureId='terminal-illness'>Terminal Illness</FeatureButton>
              </div>
            </div>
          )}

          {shouldShowSection(['increase-face-amount', 'decrease-face-amount', 'convertible', 'renewable']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Flexible Coverage</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='increase-face-amount'>Increase Face Amount</FeatureButton>
                <FeatureButton featureId='decrease-face-amount'>Decrease Face Amount</FeatureButton>
              </div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='convertible'>Convertible</FeatureButton>
                <FeatureButton featureId='renewable'>Renewable</FeatureButton>
              </div>
            </div>
          )}

          {shouldShowSection(['no-cost-wearable', 'discount-program']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Promotions</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='no-cost-wearable'>No-Cost Wearable</FeatureButton>
                <FeatureButton featureId='discount-program'>Discount Program</FeatureButton>
              </div>
            </div>
          )}

          {shouldShowSection(['accelerated-underwriting', 'instant-issue', 'non-medical']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Easy Issue</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='accelerated-underwriting'>Accelerated Underwriting</FeatureButton>
                <FeatureButton featureId='instant-issue'>Instant Issue</FeatureButton>
                <FeatureButton featureId='non-medical'>Non-Medical</FeatureButton>
              </div>
            </div>
          )}

          {shouldShowSection(['common-carrier', 'col-increase', 'family-health', 'nursing-home', 'residential-damage']) && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Additional Riders</div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='common-carrier'>Common Carrier</FeatureButton>
                <FeatureButton featureId='col-increase'>CoL Increase</FeatureButton>
                <FeatureButton featureId='family-health'>Family Health</FeatureButton>
              </div>
              <div style={styles.buttonGroup}>
                <FeatureButton featureId='nursing-home'>Nursing Home Confinement</FeatureButton>
                <FeatureButton featureId='residential-damage'>Residential Damage</FeatureButton>
              </div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button
            onClick={handleSearch}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#555';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = '#333';
            }}
            style={styles.searchResultsButton}
          >
            Search Results
          </button>
        </div>
      </div>
    </div>
  );
};

FeaturesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default FeaturesModal;
